import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, Building2, Save, RotateCcw, Plus, Copy, Sun, Moon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: '1', label: 'Lunedì', short: 'Lun' },
  { key: '2', label: 'Martedì', short: 'Mar' },
  { key: '3', label: 'Mercoledì', short: 'Mer' },
  { key: '4', label: 'Giovedì', short: 'Gio' },
  { key: '5', label: 'Venerdì', short: 'Ven' },
  { key: '6', label: 'Sabato', short: 'Sab' },
];

const DEFAULT_DAY = {
  enabled: true,
  morning: true, morning_start: '09:00', morning_end: '13:00',
  afternoon: true, afternoon_start: '14:00', afternoon_end: '18:00',
};

function buildDefaultSchedules() {
  const s = {};
  DAYS.forEach(d => { s[d.key] = { ...DEFAULT_DAY, enabled: parseInt(d.key) <= 5 }; });
  return s;
}

function DayRow({ day, config, onChange }) {
  const update = (field, value) => onChange({ ...config, [field]: value });
  const enabled = config.enabled;

  return (
    <div className={`flex items-center gap-3 py-3 px-4 rounded-lg transition-all ${enabled ? 'bg-white' : 'bg-slate-50 opacity-60'}`} data-testid={`day-row-${day.key}`}>
      {/* Day name + enabled toggle */}
      <div className="w-24 shrink-0 flex items-center gap-2">
        <Checkbox
          checked={enabled}
          onCheckedChange={(v) => update('enabled', !!v)}
          data-testid={`day-enabled-${day.key}`}
        />
        <span className={`text-sm font-semibold ${enabled ? 'text-slate-800' : 'text-slate-400'}`}>{day.label}</span>
      </div>

      {!enabled ? (
        <span className="text-xs text-slate-400 italic">Chiuso</span>
      ) : (
        <div className="flex flex-1 items-center gap-4 flex-wrap">
          {/* Morning */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => update('morning', !config.morning)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                config.morning ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'
              }`}
              data-testid={`morning-toggle-${day.key}`}
            >
              <Sun className="w-3 h-3" />
              Mattina
            </button>
            {config.morning && (
              <div className="flex items-center gap-1">
                <input type="time" value={config.morning_start} onChange={e => update('morning_start', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid={`morning-start-${day.key}`} />
                <span className="text-xs text-slate-400">-</span>
                <input type="time" value={config.morning_end} onChange={e => update('morning_end', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid={`morning-end-${day.key}`} />
              </div>
            )}
          </div>

          {/* Afternoon */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => update('afternoon', !config.afternoon)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                config.afternoon ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'
              }`}
              data-testid={`afternoon-toggle-${day.key}`}
            >
              <Moon className="w-3 h-3" />
              Pomeriggio
            </button>
            {config.afternoon && (
              <div className="flex items-center gap-1">
                <input type="time" value={config.afternoon_start} onChange={e => update('afternoon_start', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid={`afternoon-start-${day.key}`} />
                <span className="text-xs text-slate-400">-</span>
                <input type="time" value={config.afternoon_end} onChange={e => update('afternoon_end', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid={`afternoon-end-${day.key}`} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleGrid({ daySchedules, onChange, slotMinutes, onSlotChange }) {
  const [std, setStd] = useState({
    morning_start: '09:00', morning_end: '13:00',
    afternoon_start: '14:00', afternoon_end: '18:00',
  });

  const applyStandard = () => {
    const updated = {};
    DAYS.forEach(d => {
      const old = daySchedules[d.key] || {};
      updated[d.key] = {
        enabled: old.enabled !== undefined ? old.enabled : parseInt(d.key) <= 5,
        morning: true,
        morning_start: std.morning_start,
        morning_end: std.morning_end,
        afternoon: true,
        afternoon_start: std.afternoon_start,
        afternoon_end: std.afternoon_end,
      };
    });
    onChange(updated);
    toast.success('Orario standard applicato a tutti i giorni attivi');
  };

  return (
    <div className="space-y-3">
      {/* Custom standard hours bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="standard-hours-bar">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 font-medium">Orario standard della clinica</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Sun className="w-3.5 h-3.5 text-amber-500" />
            <input type="time" value={std.morning_start} onChange={e => setStd({...std, morning_start: e.target.value})}
              className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid="std-morning-start" />
            <span className="text-xs text-slate-400">-</span>
            <input type="time" value={std.morning_end} onChange={e => setStd({...std, morning_end: e.target.value})}
              className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid="std-morning-end" />
          </div>
          <div className="flex items-center gap-1.5">
            <Moon className="w-3.5 h-3.5 text-indigo-500" />
            <input type="time" value={std.afternoon_start} onChange={e => setStd({...std, afternoon_start: e.target.value})}
              className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid="std-afternoon-start" />
            <span className="text-xs text-slate-400">-</span>
            <input type="time" value={std.afternoon_end} onChange={e => setStd({...std, afternoon_end: e.target.value})}
              className="px-2 py-1 border border-slate-200 rounded text-xs w-24" data-testid="std-afternoon-end" />
          </div>
          <button
            type="button"
            onClick={applyStandard}
            className="flex items-center gap-1 px-3 py-1.5 bg-sky-500 text-white rounded-lg text-xs font-medium hover:bg-sky-600 transition-colors"
            data-testid="apply-standard-btn"
          >
            <Copy className="w-3 h-3" />
            Applica a tutti
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 font-medium">Personalizza orari per ogni giorno</p>

      <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
        {DAYS.map(day => (
          <DayRow
            key={day.key}
            day={day}
            config={daySchedules[day.key] || { ...DEFAULT_DAY, enabled: false }}
            onChange={(updated) => onChange({ ...daySchedules, [day.key]: updated })}
          />
        ))}
      </div>

      <div className="pt-2">
        <label className="block text-xs font-medium text-slate-600 mb-1">Durata appuntamento</label>
        <select value={slotMinutes} onChange={e => onSlotChange(parseInt(e.target.value))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-48" data-testid="slot-duration">
          <option value={15}>15 minuti</option>
          <option value={20}>20 minuti</option>
          <option value={30}>30 minuti</option>
          <option value={45}>45 minuti</option>
          <option value={60}>60 minuti</option>
          <option value={90}>90 minuti</option>
        </select>
      </div>
    </div>
  );
}

export default function ScheduleSettings() {
  const { token } = useAuth();
  const [clinicSchedule, setClinicSchedule] = useState({ slot_minutes: 30, day_schedules: buildDefaultSchedules() });
  const [deptOverrides, setDeptOverrides] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingDept, setAddingDept] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [schedRes, deptSchedRes, deptsRes] = await Promise.all([
        fetch(`${API}/api/schedule/clinic`, { headers }),
        fetch(`${API}/api/schedule/departments`, { headers }),
        fetch(`${API}/api/departments`, { headers }),
      ]);
      if (schedRes.ok) {
        const data = await schedRes.json();
        setClinicSchedule({
          slot_minutes: data.slot_minutes || 30,
          day_schedules: data.day_schedules || buildDefaultSchedules(),
        });
      }
      if (deptSchedRes.ok) setDeptOverrides(await deptSchedRes.json());
      if (deptsRes.ok) setDepartments(await deptsRes.json());
    } catch (e) { toast.error('Errore rete'); } finally { setLoading(false); }
  };

  const saveClinicSchedule = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/schedule/clinic`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(clinicSchedule)
      });
      if (res.ok) toast.success('Orari clinica salvati!');
      else toast.error('Errore salvataggio');
    } catch { toast.error('Errore rete'); }
    finally { setSaving(false); }
  };

  const saveDeptSchedule = async (deptId, data) => {
    try {
      const res = await fetch(`${API}/api/schedule/department/${deptId}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) toast.success('Orari reparto salvati!');
      else toast.error('Errore');
    } catch { toast.error('Errore rete'); }
  };

  const deleteDeptSchedule = async (deptId) => {
    try {
      await fetch(`${API}/api/schedule/department/${deptId}`, { method: 'DELETE', headers });
      setDeptOverrides(deptOverrides.filter(d => d.department_id !== deptId));
      toast.success('Override rimosso');
    } catch { toast.error('Errore'); }
  };

  const addDeptOverride = () => {
    if (!addingDept) return;
    const dept = departments.find(d => d.id === addingDept);
    if (!dept) return;
    if (deptOverrides.some(o => o.department_id === addingDept)) { toast.error('Reparto gia configurato'); return; }
    setDeptOverrides([...deptOverrides, {
      department_id: addingDept,
      department_name: dept.name,
      enabled: true,
      slot_minutes: clinicSchedule.slot_minutes,
      day_schedules: JSON.parse(JSON.stringify(clinicSchedule.day_schedules)),
    }]);
    setAddingDept('');
  };

  const updateOverride = (deptId, field, value) => {
    setDeptOverrides(deptOverrides.map(o =>
      o.department_id === deptId ? { ...o, [field]: value } : o
    ));
  };

  const unconfiguredDepts = departments.filter(d => !deptOverrides.some(o => o.department_id === d.id));

  if (loading) return <Layout><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-5xl" data-testid="schedule-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Clock className="w-6 h-6 text-sky-500" /> Orari e Disponibilit&agrave;
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configura orari specifici per ogni giorno della settimana</p>
        </div>

        {/* Clinic Schedule */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="clinic-schedule">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-sky-100 rounded-xl"><Clock className="w-5 h-5 text-sky-600" /></div>
            <div>
              <h2 className="font-semibold text-slate-800">Orari Generali Clinica</h2>
              <p className="text-xs text-slate-500">Orari predefiniti per tutti i reparti senza override</p>
            </div>
          </div>
          <ScheduleGrid
            daySchedules={clinicSchedule.day_schedules}
            onChange={(ds) => setClinicSchedule({ ...clinicSchedule, day_schedules: ds })}
            slotMinutes={clinicSchedule.slot_minutes}
            onSlotChange={(sm) => setClinicSchedule({ ...clinicSchedule, slot_minutes: sm })}
          />
          <Button onClick={saveClinicSchedule} disabled={saving} className="mt-5 bg-sky-500 hover:bg-sky-600" data-testid="save-clinic-schedule">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Salvataggio...' : 'Salva Orari Clinica'}
          </Button>
        </div>

        {/* Department Overrides */}
        <div className="mb-4">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-violet-500" /> Orari per Reparto
            <span className="text-xs text-slate-400 font-normal">opzionale — sovrascrive gli orari clinica</span>
          </h2>
        </div>

        {unconfiguredDepts.length > 0 && (
          <div className="flex gap-2 mb-4">
            <select value={addingDept} onChange={e => setAddingDept(e.target.value)}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="add-dept-select">
              <option value="">Aggiungi orari specifici per un reparto...</option>
              {unconfiguredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <Button onClick={addDeptOverride} disabled={!addingDept} variant="outline" data-testid="add-dept-btn">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>
        )}

        {deptOverrides.length === 0 ? (
          <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nessun orario specifico per reparto</p>
            <p className="text-xs text-slate-400 mt-1">Tutti i reparti usano gli orari generali della clinica</p>
          </div>
        ) : (
          <div className="space-y-4">
            {deptOverrides.map(ov => (
              <div key={ov.department_id} className="bg-white rounded-xl border border-violet-200 p-5" data-testid={`dept-schedule-${ov.department_id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="font-semibold text-sm text-violet-700">{ov.department_name}</h3>
                  <span className="text-xs text-slate-400">Override orari</span>
                </div>
                <ScheduleGrid
                  daySchedules={ov.day_schedules || buildDefaultSchedules()}
                  onChange={(ds) => updateOverride(ov.department_id, 'day_schedules', ds)}
                  slotMinutes={ov.slot_minutes || 30}
                  onSlotChange={(sm) => updateOverride(ov.department_id, 'slot_minutes', sm)}
                />
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => saveDeptSchedule(ov.department_id, ov)} className="bg-violet-500 hover:bg-violet-600">
                    <Save className="w-3 h-3 mr-1" /> Salva
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteDeptSchedule(ov.department_id)} className="text-red-500 hover:bg-red-50">
                    <RotateCcw className="w-3 h-3 mr-1" /> Usa default clinica
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
